// src/components/settings/EsimFiles.jsx — a work number's eSIM QR files (scheduler 0038, owner 2026-10-10).
//
// ONE component, two surfaces: Admin → Work Phone Numbers (canManage: upload / replace / retire) and the
// agent modal's work-number field, where a manager picks a number (view only). Which files a viewer
// receives is RLS (= the numbers they can see), not this component.
//
// "Replace" = upload the new QR and retire the live one(s). Nothing is deleted: a retired file stays in
// the list under "earlier", openable, and can be restored.
import { useState, useRef } from 'react'
import { t, tLog, getLanguage } from '../../lib/i18n'
import { uploadPhoneFile, setPhoneFileRetired, phoneFileUrl } from '../../db/index.js'
import { fmtPhone } from '../../lib/phone.js'
import { Modal, pill } from '../desk/primitives.jsx'
import { PhoneLoginsLoader } from './PortalLogins.jsx'

const fmtDate = iso => iso
  ? new Date(iso).toLocaleDateString(getLanguage() === 'ka' ? 'ka-GE' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  : ''

// Open in a new tab. The tab is opened synchronously in the click so the popup blocker allows it,
// then pointed at the signed URL once it exists.
async function openFile(f, setErr) {
  const w = window.open('', '_blank')
  try {
    const url = await phoneFileUrl(f.storage_path)
    if (w) w.location.href = url; else window.location.href = url
  } catch (e) {
    if (w) w.close()
    setErr(e.message || String(e))
  }
}

// The row button: opens the number's details — its myhome/ss logins (0039) and its eSIM QR files.
// `files` = every file of THIS number (live and retired), newest first.
export function EsimQrButton({ phone, files = [], canManage = false, onChanged }) {
  const [open, setOpen] = useState(false)
  const live = files.filter(f => !f.retired_at)
  const has = live.length > 0
  return (
    <>
      <button type="button" onClick={e => { e.stopPropagation(); setOpen(true) }} data-esim-qr={phone.number}
        title={t('number_details_title')}
        className={pill('none', { door: true, extra: `border whitespace-nowrap ${has
          ? 'border-primary/40 text-primary hover:bg-primary/10'
          : 'border-dashed border-border text-muted-foreground hover:text-foreground'}` })}>
        🔑 ▦ QR{live.length > 1 ? ` ${live.length}` : ''}
      </button>
      {open && <EsimFilesModal phone={phone} files={files} canManage={canManage} onChanged={onChanged} onClose={() => setOpen(false)} />}
    </>
  )
}

export function EsimFilesModal({ phone, files, canManage, onChanged, onClose }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [showOld, setShowOld] = useState(false)
  const fileRef = useRef(null)
  const modeRef = useRef('add')
  const live = files.filter(f => !f.retired_at)
  const old = files.filter(f => f.retired_at)

  const pick = mode => { modeRef.current = mode; setErr(''); fileRef.current?.click() }
  const onPicked = async e => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setBusy(true); setErr('')
    try { await uploadPhoneFile(phone.id, file, { replace: modeRef.current === 'replace' }); await onChanged?.() }
    catch (x) { setErr(x.message || String(x)) }
    finally { setBusy(false) }
  }
  const retire = async (f, retired) => {
    if (retired && !window.confirm(tLog('esim_qr_retire_confirm', { name: f.file_name }))) return
    setBusy(true); setErr('')
    try { await setPhoneFileRetired(f.id, retired); await onChanged?.() }
    catch (x) { setErr(x.message || String(x)) }
    finally { setBusy(false) }
  }

  const row = (f, isOld) => (
    <li key={f.id} className={`flex items-center gap-2 rounded-md px-2 py-1.5 ${isOld ? 'bg-muted/20 text-muted-foreground' : 'bg-muted/40'}`} data-esim-file={f.id}>
      <button type="button" onClick={() => openFile(f, setErr)} className="flex-1 min-w-0 text-left hover:underline">
        <span className="block truncate text-sm">{f.file_name}</span>
        <span className="block text-[11px] text-muted-foreground">
          {fmtDate(f.issued_at)}{isOld ? ` · ${t('esim_qr_retired')} ${fmtDate(f.retired_at)}` : ''}
        </span>
      </button>
      <button type="button" onClick={() => openFile(f, setErr)} className="text-xs px-2 py-1 rounded-md border border-border hover:border-primary/40">
        {t('esim_qr_open')}
      </button>
      {canManage && (
        <button type="button" disabled={busy} onClick={() => retire(f, !isOld)}
          className="text-xs px-1.5 text-muted-foreground hover:text-foreground disabled:opacity-50"
          title={isOld ? t('esim_qr_restore') : t('esim_qr_retire')}>
          {isOld ? '↺' : '✕'}
        </button>
      )}
    </li>
  )

  return (
    <Modal title={fmtPhone(phone.number)} onClose={onClose}>
      <div className="space-y-3" data-esim-modal>
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('portal_section')}</div>
        <PhoneLoginsLoader phoneId={phone.id} />
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-2 border-t border-border">eSIM QR</div>
        {live.length === 0
          ? <div className="text-sm text-muted-foreground italic">{t('esim_qr_none')}</div>
          : <ul className="space-y-1.5">{live.map(f => row(f, false))}</ul>}

        {canManage && (
          <div className="flex flex-wrap gap-2">
            <input ref={fileRef} type="file" accept="application/pdf,image/png,image/jpeg" className="hidden" onChange={onPicked} />
            {live.length > 0 && (
              <button type="button" disabled={busy} onClick={() => pick('replace')} data-esim-replace
                className="text-sm px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50">
                {busy ? '…' : t('esim_qr_replace')}
              </button>
            )}
            <button type="button" disabled={busy} onClick={() => pick('add')} data-esim-add
              className="text-sm px-3 py-1.5 rounded-md border border-border hover:border-primary/40 disabled:opacity-50">
              {busy ? '…' : live.length > 0 ? t('esim_qr_add_another') : t('esim_qr_upload')}
            </button>
          </div>
        )}
        {canManage && live.length > 0 && <div className="text-[11px] text-muted-foreground">{t('esim_qr_replace_hint')}</div>}

        {err && <div className="text-xs text-red-600">{err}</div>}

        {old.length > 0 && (
          <div>
            <button type="button" onClick={() => setShowOld(s => !s)} className="text-xs text-muted-foreground hover:text-foreground">
              {showOld ? '▴' : '▸'} {tLog('esim_qr_earlier', { n: old.length })}
            </button>
            {showOld && <ul className="space-y-1.5 mt-1.5">{old.map(f => row(f, true))}</ul>}
          </div>
        )}
      </div>
    </Modal>
  )
}
